import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L, { Icon } from 'leaflet';
import { MapPickerProps, MapLocation } from '../../types/maps';
import { reverseGeocode } from '../../utils/geocoding';
import 'leaflet/dist/leaflet.css';
import './MapPicker.css';

// Import marker icons
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { ReportStatus } from '../../types/illegalParkingReport';

// Fix Leaflet default icon issue
const defaultIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

// Custom marker icons
const createCustomIcon = (color: string, shape: 'circle' | 'triangle') => {
  const svgContent = shape === 'circle' 
    ? `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="12" r="3" fill="white"/>
      </svg>`
    : `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <polygon points="12,2 22,22 2,22" fill="${color}" stroke="white" stroke-width="1"/>
        <circle cx="12" cy="16" r="2" fill="white"/>
      </svg>`;

  return new Icon({
    iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent),
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const MARKER_ICONS = {
  [ReportStatus.CONFIRMED]: createCustomIcon('#10B981', 'circle'), // Green circle with white center
  [ReportStatus.SUBMITTED]: createCustomIcon('#F59E0B', 'triangle'), // Orange triangle with white center
  default: defaultIcon
};

// Interactive marker component for review mode
const DraggableMarker: React.FC<{
  position: [number, number];
  onLocationSelect: (location: MapLocation) => void;
}> = ({ position, onLocationSelect }) => {
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(position);

  // Sync marker position with prop changes
  useEffect(() => {
    setMarkerPosition(position);
  }, [position]);

  const handleDragEnd = async (e: any) => {
    const { lat, lng } = e.target.getLatLng();
    setMarkerPosition([lat, lng]);
    const geocodeResult = await reverseGeocode(lat, lng);
    onLocationSelect({
      lat,
      lng,
      address: geocodeResult?.address,
      formattedAddress: geocodeResult?.formattedAddress
    });
  };

  return (
    <Marker
      position={markerPosition}
      draggable={true}
      icon={defaultIcon}
      eventHandlers={{
        dragend: handleDragEnd
      }}
    />
  );
};

// Map event handler component
const MapEventHandler: React.FC<{
  onLocationSelect: (location: MapLocation) => void;
  setMarkerPosition: (pos: [number, number]) => void;
  onBoundsChange?: (bounds: { south: number; north: number; west: number; east: number }) => void;
  readOnly?: boolean;
}> = React.memo(({ onLocationSelect, setMarkerPosition, onBoundsChange, readOnly }) => {
  const map = useMapEvents({
    click: async (e: any) => {
      if (!readOnly) {
        const { lat, lng } = e.latlng;
        setMarkerPosition([lat, lng]);
        const geocodeResult = await reverseGeocode(lat, lng);
        onLocationSelect({
          lat,
          lng,
          address: geocodeResult?.address,
          formattedAddress: geocodeResult?.formattedAddress
        });
      }
    },
    moveend: () => {
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          south: bounds.getSouth(),
          north: bounds.getNorth(),
          west: bounds.getWest(),
          east: bounds.getEast()
        });
      }
    },
    zoomend: () => {
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          south: bounds.getSouth(),
          north: bounds.getNorth(),
          west: bounds.getWest(),
          east: bounds.getEast()
        });
      }
    }
  });

  return null;
});

const MapPicker: React.FC<MapPickerProps> = ({
  onLocationSelect,
  onBoundsChange,
  initialLocation,
  className = '',
  height = '300px',
  placeholder = 'Loading map...',
  markers = [],
  readOnly = false
}) => {
  const defaultLocation: MapLocation = {
    lat: 40.712800, // Using 6 decimal places for precision
    lng: -74.006000
  };

  const mapLocation = initialLocation || defaultLocation;
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([
    mapLocation.lat,
    mapLocation.lng
  ]);
  const mapRef = useRef<L.Map>(null);

  // Set default icon for all markers
  useEffect(() => {
    // @ts-ignore
    delete Icon.Default.prototype._getIconUrl;
    Icon.Default.mergeOptions({
      iconUrl: markerIcon,
      iconRetinaUrl: markerIcon2x,
      shadowUrl: markerShadow,
    });
  }, []);

  // Update marker position when initialLocation changes
  useEffect(() => {
    if (initialLocation) {
      setMarkerPosition([initialLocation.lat, initialLocation.lng]);
    }
  }, [initialLocation]);

  const handleLocationSelect = useCallback((location: MapLocation) => {
    setMarkerPosition([location.lat, location.lng]);
    onLocationSelect?.(location);
  }, [onLocationSelect]);

  const LocationMarker = () => {
    return markerPosition === null ? null : (
      <Marker
        position={markerPosition}
        icon={defaultIcon}
      />
    );
  };

  useEffect(() => {
    if (markerPosition && mapRef.current && !readOnly) {
      mapRef.current.flyTo(markerPosition, 15);
    }
  }, [markerPosition, readOnly]);

  return (
    <div className={`map-picker ${className}`} style={{ height }}>
      <MapContainer
        center={[mapLocation.lat, mapLocation.lng]}
        zoom={15}
        style={{ height: '100%' }}
        className="map-container"
        ref={mapRef}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {readOnly ? (
          // Read-only mode: show all markers
          <>
            {markers.map((marker) => (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={marker.type ? MARKER_ICONS[marker.type] : MARKER_ICONS.default}
                eventHandlers={{
                  click: () => marker.onClick?.()
                }}
              />
            ))}
            <MapEventHandler
              onLocationSelect={handleLocationSelect}
              setMarkerPosition={setMarkerPosition}
              onBoundsChange={onBoundsChange}
              readOnly={readOnly}
            />
          </>
        ) : (
          // Interactive mode: show single draggable marker
          <>
            <DraggableMarker
              position={markerPosition}
              onLocationSelect={handleLocationSelect}
            />
            <MapEventHandler
              onLocationSelect={handleLocationSelect}
              setMarkerPosition={setMarkerPosition}
              onBoundsChange={onBoundsChange}
              readOnly={readOnly}
            />
            <LocationMarker />
          </>
        )}
      </MapContainer>

      {!readOnly && (
        <div className="map-instructions">
          <div className="instruction-content">
            <svg className="instruction-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>Click on the map to place a marker or drag to adjust</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPicker; 